import type { PageHelp } from "../types";

export const salesPageHelp: Record<string, PageHelp> = {
  today: {
    title: "Today",
    summary:
      "Your daily starting point. It shows fresh leads to call, the single most useful thing to do right now, your key numbers, and today's booked calls.",
    steps: [
      { title: "Check Fresh leads — dial now", body: "If this box appears, new leads from the last 24 hours are waiting. Click a row to open the prospect, then call them." },
      { title: "Read The one thing", body: "Flowio tells you what to do first, for example confirm bookings or prep for today's calls. Press the button beside it to go there." },
      { title: "Scan the four numbers", body: "MRR, Show rate, Close rate and Sets today. Click any card to open the page where that number comes from." },
      { title: "Toggle First touch only", body: "Above the numbers. On: Show rate and Close rate count only each account's first-ever booking or call — a follow-up showing or closing again doesn't add in." },
      { title: "Work Calls today", body: "Click a call to open the prospect. The badge says confirmed or confirm." },
      { title: "Clear Follow-ups due", body: "Every account whose most recent logged call is still Follow-up, oldest due first. Click one to open the account." },
      { title: "Use Quick actions", body: "Shortcuts to the next thing to do, such as confirming bookings or opening the Live Call cockpit." },
    ],
    tips: [
      "Speed matters: the app cites a 22 times better conversion when a lead is called within 5 minutes.",
      "The lead age pill is green under 5 minutes, orange under an hour, grey after that.",
      "If MRR shows no goal, an admin can set one in Settings, Content.",
    ],
    articles: ["sales-start-your-day", "sales-today-reference"],
  },

  pipeline: {
    title: "Pipeline",
    summary:
      "Every account from first lead to paying customer, as a board with one column per stage. Use it to see who is where and to add new prospects.",
    steps: [
      { title: "Read the columns", body: "Each column is a stage. The number beside the name is how many accounts are in it." },
      { title: "Open an account", body: "Click a card to open that account's overview page." },
      { title: "Click New prospect", body: "A panel slides in from the right." },
      { title: "Choose the fastest way to add", body: "Paste a link under Add from report link or Add from Google Maps and click Add, or fill in the form manually." },
      { title: "Fill in the form", body: "Account name is required. Set Status and, if you know them, Rev/mo $ and Ticket $. Then click Add prospect." },
    ],
    tips: [
      "If your agency mirrors a GoHighLevel pipeline, move cards in GoHighLevel and then sync. Moving them here is not possible.",
      "The 'per month booked' line under a column adds up real subscription revenue, not estimates.",
      "Add revenue and ticket when you know them. They feed the Revenue Calculator.",
    ],
    articles: ["sales-add-a-prospect", "sales-pipeline-reference"],
  },

  "live-call": {
    title: "Live Call",
    summary:
      "Keep this open during a sales call: Constantine's call structure as open cards, all in view, with just the boxes to fill in and tick off, in any order. Each card turns amber when started and green when done.",
    steps: [
      { title: "Pick the account", body: "Choose the prospect in the header, or open Live Call from the prospect panel. Prospect, shop and date fill in." },
      { title: "Open the call brief", body: "Click Call brief for the account's research in a window that floats over your video call." },
      { title: "Start the timer", body: "Press play or the Space bar." },
      { title: "Fill in whatever they're talking about", body: "Each box is labelled with the question to ask, in bold. Type and tick right on the card for whatever the prospect raises. Tap a card's title for what to say. Grey is not yet, amber is started, green is done." },
      { title: "Get their numbers", body: "In The phones and The numbers, type what they tell you. Recoverable revenue shows once all six numbers are in." },
      { title: "Read it back", body: "The Transition card has the summary built from their answers. Read it to them and get a yes." },
      { title: "Handle pushback", body: "Tap Objections in the header, or Not a fit? if they're not right for you." },
      { title: "Finish", body: "Click Copy summary and paste it into Slack or the GHL contact." },
    ],
    tips: [
      "The card saves to the account as you go, so it's the same on your phone and laptop.",
      "Mark covered turns a topic green when you've dealt with it without filling every line.",
      "New call asks before it clears anything.",
    ],
    articles: ["sales-run-a-live-call", "sales-live-call-reference"],
  },

  "missed-call-math": {
    title: "Revenue Calculator",
    summary:
      "Two tabs for a screen share: Missed calls turns the calls he misses into a monthly dollar figure; Google Maps forecast shows how many extra customers he gets from reaching Google's top 3, and roughly when.",
    steps: [
      { title: "Set Calls missed / day", body: "Use the minus and plus buttons for how many calls the business misses on an average day." },
      { title: "Set Days open / week", body: "The range is 4 to 7 days, in half-day steps." },
      { title: "Adjust the two sliders", body: "Callers who book is the share of missed callers who would book. Bookings that become paid jobs is the share of those that turn into work." },
      { title: "Enter Average job", body: "This is the dollar value of one job for the prospect." },
      { title: "Choose Show totals over", body: "Pick 1 to 24 months. The headline number covers that many months." },
      { title: "Present it", body: "Click Presentation mode for a full-screen results view, or Pop out for a separate window to share." },
    ],
    tips: [
      "Sliders start at 50 percent. The flag button beside each slider allows lower values.",
      "Break-even, ROI and Payback show a dash until your prices are set in Settings → Content and his average job is entered.",
      "Numbers are shared between both tabs and Live Call, so edits in one place show in the others.",
      "The Google Maps forecast tab has its own guide: tap any number there to see how it's worked out.",
    ],
    articles: ["sales-show-missed-call-math", "sales-show-maps-forecast", "sales-missed-call-math-reference"],
  },

  videos: {
    title: "VSL Hosting",
    summary:
      "Host sales videos (VSLs) on your own account and get a link and an embed code to put on any landing page. You can upload a file or generate one with HeyGen.",
    steps: [
      { title: "Click Upload video", body: "Choose a video file. MP4 with H.264 video and AAC audio plays everywhere." },
      { title: "Wait for the upload", body: "A progress bar shows. Leave the tab open until it reaches 100 percent." },
      { title: "Check it with Preview", body: "Preview opens the player page in a new tab." },
      { title: "Copy what you need", body: "Copy link gives a direct link. Copy embed code gives HTML to paste into a page builder's embed block." },
      { title: "Or create one with HeyGen", body: "Under Create with HeyGen, click Write a script → get a video, choose a mode, and click Generate video." },
    ],
    tips: [
      "Deleting a video makes every page that embeds it stop working.",
      "HeyGen videos take a few minutes and go live on their own when ready.",
      "HeyGen needs an API key in Settings and uses your HeyGen credits.",
    ],
    articles: ["sales-host-a-video"],
  },

  "contract-builder": {
    title: "Contract Builder",
    summary:
      "Builds the service agreement for a deal. Switch parts of the offer on or off, fill in the client details, watch the document update, then export it.",
    steps: [
      { title: "Pick the account and click Prefill", body: "This copies the client's details from their account and from the Contract details form they filled in on the portal." },
      { title: "Set the Service scope", body: "Turn Lead-gen / CRM service, the guarantee, Paid advertising management and Website deliverable on or off." },
      { title: "Complete Client & deal", body: "Legal entity name, state, entity type, DBA, address, agreement date and notice email." },
      { title: "Check Fees & term", body: "Set the fees and Initial term (months)." },
      { title: "Review the paper preview", body: "The document on the right changes as you type. Empty items show as blanks." },
      { title: "Export", body: "Click Export .docx for a Word file, or .txt for plain text." },
    ],
    tips: [
      "Your work is saved in this browser as a draft, so it is still there next time.",
      "If Ads are on, you must enter the minimum monthly Ad Spend before you can export.",
      "Check the Provider & governing law section. It comes pre-filled and may not be your agency's details.",
    ],
    articles: ["sales-build-a-contract", "sales-contract-builder-reference"],
  },
};
